import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChevronLeft, 
  MapPin, 
  Calendar, 
  Download, 
  Truck,
  Lightbulb,
  Maximize,
  CheckCircle,
  XCircle,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BookingDetailPage = () => {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const { token } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my/bookings/${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my/bookings/${bookingId}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${booking?.invoice_number || bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', className: 'status-pending' },
      paid: { label: 'Bezahlt - Warte auf Bestätigung', className: 'status-paid' },
      confirmed: { label: 'Bestätigt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Abgelehnt', className: 'bg-red-50 text-red-700 border-red-200' },
      cancelled: { label: 'Storniert', className: 'status-cancelled' },
      expired: { label: 'Abgelaufen', className: 'status-expired' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Buchung nicht gefunden</h2>
          <Link to="/my-bookings">
            <Button>Zurück zu meinen Buchungen</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 lg:py-12 bg-[hsl(var(--surface-2))] min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Back Link */}
          <Link 
            to="/my-bookings" 
            className="inline-flex items-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Zurück zu meinen Buchungen
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Booking Header */}
              <Card className="card-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-2xl font-semibold">Buchungsdetails</h1>
                        {getStatusBadge(booking.status)}
                      </div>
                      {booking.invoice_number && (
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                          Rechnungsnr.: {booking.invoice_number}
                        </p>
                      )}
                    </div>
                    {(booking.status === 'paid' || booking.status === 'confirmed') && (
                      <Button onClick={downloadInvoice} data-testid="download-invoice-button">
                        <Download className="w-4 h-4 mr-2" />
                        Rechnung herunterladen
                      </Button>
                    )}
                  </div>

                  {/* Location Info */}
                  <div className="flex items-start gap-4 p-4 bg-[hsl(var(--muted))] rounded-lg">
                    <div className="w-12 h-12 bg-[hsl(var(--primary)/0.1)] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{booking.location?.name}</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {booking.location?.address}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Period */}
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Buchungszeitraum
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Start</p>
                      <p className="text-lg font-semibold">
                        {format(new Date(booking.start_date), 'dd. MMMM yyyy', { locale: de })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Ende</p>
                      <p className="text-lg font-semibold">
                        {format(new Date(booking.end_date), 'dd. MMMM yyyy', { locale: de })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trailer Info */}
              {booking.trailer && (
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Truck className="w-5 h-5 mr-2" />
                      Anhänger-Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">{booking.trailer.name}</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <Maximize className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <span>{booking.trailer.size_m2} m²</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <span>{booking.trailer.has_lighting ? 'Mit Beleuchtung' : 'Ohne Beleuchtung'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <span>{booking.trailer.is_mobile ? 'Mobil' : 'Stationär'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Services */}
              {booking.service_details && booking.service_details.length > 0 && (
                <Card className="card-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      Zusatzservices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {booking.service_details.map((service) => (
                        <div key={service.id} className="flex items-center justify-between p-3 bg-[hsl(var(--muted))] rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-[hsl(var(--chart-2))]" />
                            <div>
                              <p className="font-medium">{service.name}</p>
                              <p className="text-sm text-[hsl(var(--muted-foreground))]">{service.description}</p>
                            </div>
                          </div>
                          <span className="font-semibold">CHF {formatPrice(service.price)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Price Summary */}
            <div>
              <Card className="card-shadow sticky top-24">
                <CardHeader>
                  <CardTitle>Preiszusammenfassung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--muted-foreground))]">Werbefläche</span>
                    <span>CHF {formatPrice(booking.base_price || booking.total_price)}</span>
                  </div>
                  {booking.services_price > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">Zusatzservices</span>
                      <span>CHF {formatPrice(booking.services_price)}</span>
                    </div>
                  )}
                  {booking.discount_amount > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Rabatt {booking.discount_code && `(${booking.discount_code})`}</span>
                      <span>- CHF {formatPrice(booking.discount_amount)}</span>
                    </div>
                  )}
                  <div className="border-t border-[hsl(var(--border))] pt-4">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Gesamtbetrag</span>
                      <span>CHF {formatPrice(booking.total_price)}</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">inkl. MwSt.</p>
                  </div>

                  {(booking.status === 'paid' || booking.status === 'confirmed') && booking.paid_at && (
                    <div className="pt-4 border-t border-[hsl(var(--border))]">
                      <div className="flex items-center gap-2 text-[hsl(var(--chart-2))]">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Bezahlt am</span>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                        {format(new Date(booking.paid_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </p>
                      {booking.status === 'confirmed' && booking.confirmed_at && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Bestätigt am</span>
                          </div>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                            {format(new Date(booking.confirmed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {booking.status === 'rejected' && (
                    <div className="pt-4 border-t border-[hsl(var(--border))]">
                      <div className="flex items-center gap-2 text-red-600">
                        <XCircle className="w-5 h-5" />
                        <span className="font-medium">Abgelehnt</span>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                        Diese Buchung wurde leider abgelehnt. Bitte kontaktieren Sie uns bei Fragen.
                      </p>
                    </div>
                  )}

                  {booking.completion_photo_url && (
                    <div className="pt-4 border-t border-[hsl(var(--border))]" data-testid="completion-photo-section">
                      <div className="flex items-center gap-2 text-emerald-600 mb-3">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Montiert!</span>
                      </div>
                      <img
                        src={`${API_URL}${booking.completion_photo_url}`}
                        alt="Montiertes Plakat"
                        className="w-full rounded-lg border border-[hsl(var(--border))]"
                      />
                      {booking.completion_message && (
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 italic">"{booking.completion_message}"</p>
                      )}
                      {booking.completed_at && (
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                          {format(new Date(booking.completed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
